import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Theme provider — persists the light/dark preference in localStorage
 * and syncs it to `<html data-theme="...">`, which the CSS uses to
 * swap the palette. Deliberately does NOT auto-follow the OS `prefers-
 * color-scheme` because editors switch OS themes as ambient light
 * changes — Newsdesk should stay put unless they explicitly toggle it.
 *
 * The initial script in `index.html` sets the attribute BEFORE React
 * hydrates so we never get a flash of the wrong theme on reload.
 */

const STORAGE_KEY = "ggn.theme"; // "light" | "dark"

function readInitial() {
  if (typeof window === "undefined") return "light";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "dark" || v === "light") return v;
  } catch {
    // localStorage blocked (Safari private mode) — fall through
  }
  // First-visit default: respect the OS preference ONCE, then persist
  // whatever the user does. Feels more considered than defaulting to
  // light for someone whose whole environment is dark.
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

const ThemeContext = createContext({
  theme: "light",
  toggle: () => {},
  setTheme: () => {},
});

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitial);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.setAttribute("data-theme", "dark");
    else root.removeAttribute("data-theme");
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore — will just not persist across reloads
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next === "dark" ? "dark" : "light");
  }, []);
  const toggle = useCallback(() => {
    setThemeState((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggle, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
